<template>
    <div class="file-upload"
         ref="fileUploadComponent"
         @dragover="dragover"
         @dragleave="dragleave"
         @drop="drop"
         @click="onFileUploadOpenEvent"
    >
        <div class="file-upload-wrapper">
            <label :for="id"
                   v-html="description"
            ></label>
        </div>
    </div>
    <input type="file"
           :multiple="multiple"
           name="fields[assetsFieldHandle][]"
           :id="id"
           :ref="id"
           class="file-upload-input"
           :accept="accept"
           @change="onFileChangeEvent"
    />
    <div v-if="!!getFilesToUpload.length"
         v-cloak
         class="file-upload-list"
    >
        <h3>{{ listPlaceholder }}</h3>
        <ul>
            <li v-for="(file, index) in getFilesToUpload"
                :key="index"
            >
                <span>
                    <i class="uil"
                       :class="resolveAttachmentIcon(file.file_type)"
                    ></i>
                    {{ file.name }}
                </span>
                <operations-holder>
                    <operations-remove-button :key='$options.uuidV4()'
                                              :value="file"
                                              @confirm="onRemoveSelectedFileEvent"
                    />
                </operations-holder>
            </li>
        </ul>
    </div>
    <div v-if="!!getUploadedFiles.length"
         v-cloak
         class="file-upload-list"
    >
        <h3>{{ uploadedFilesPlaceholder }}</h3>
        <ul>
            <li v-for="(file, index) in getUploadedFiles"
                :key="index"
            >
                <a v-if="!!previewFiles"
                   href="#"
                   @click.prevent="onFilePreviewEvent(file)"
                >
                    <i class="uil"
                       :class="resolveAttachmentIcon(file.file_type)"
                    ></i>
                    {{ file.original_file_name }}
                </a>
                <span v-else>
                    <i class="uil"
                       :class="resolveAttachmentIcon(file.file_type)"
                    ></i>
                    {{ file.original_file_name }}
                </span>
                <operations-holder v-if="!!downloadFiles || !!deleteFiles">
                    <operations-download-button v-if="!!downloadFiles"
                                                :key='$options.uuidV4()'
                                                :value="file"
                                                @download="onUploadedFileDownloadEvent"
                    />
                    <operations-remove-button v-if="!!deleteFiles"
                                              :key='$options.uuidV4()'
                                              :value="file"
                                              @confirm="onUploadedFileRemoveEvent"
                    />
                </operations-holder>
            </li>
        </ul>
    </div>
</template>

<script>
import Translate from '../../../../Src/Modules/Localization/Translate';
import OperationsRemoveButton from '../../List/Operations/OperationsRemoveButton';
import { cloneDeep } from 'lodash/lang';
import uuidV4 from '../../../../Src/Utils/UuidV4';
import OperationsHolder from '../../List/Operations/OperationsHolder';
import OperationsDownloadButton from '../../List/Operations/OperationsDownloadButton';

export default {
    name: 'FileUpload',
    uuidV4,
    inject: [
        'resolveAttachmentIcon',
    ],
    components: {
        OperationsHolder,
        OperationsRemoveButton,
        OperationsDownloadButton,
    },
    emits: [
        'preview-uploaded-file',
        'download-uploaded-file',
        'remove-uploaded-file',
        'update',
    ],
    props: {
        value: {
            type: Array,
            default: () => ([])
        },
        input: {},
        accept: {
            type: String,
            default: null
        },
        name: {
            type: String,
            default: 'name'
        },
        id: {
            type: String,
            required: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        description: {
            type: String,
            default: Translate('placeholders.choose_file')
        },
        listPlaceholder: {
            type: String,
            default: Translate('general_messages.selected_files')
        },
        uploadedFilesPlaceholder: {
            type: String,
            default: Translate('general_messages.uploaded_files')
        },
        previewFiles: {
            type: Boolean,
            default: false
        },
        downloadFiles: {
            type: Boolean,
            default: false
        },
        deleteFiles: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        getFilesToUpload() {
            if (!this.value) {
                return [];
            }

            if (!this.value.length) {
                return [];
            }

            return this.value.filter(item => !item.hasOwnProperty('id'));
        },
        getUploadedFiles() {
            if (!this.value) {
                return [];
            }

            if (!this.value.length) {
                return [];
            }

            return this.value.filter(item => !!item.hasOwnProperty('id'));
        }
    },
    methods: {
        onFileUploadOpenEvent(event) {
            event.preventDefault();

            this.$refs[this.id].click()
        },
        onFileChangeEvent(event) {
            const eventFiles = !!event.dataTransfer
                ? event.dataTransfer.files
                : event.target.files;

            const files = !this.multiple
                ? [...eventFiles]
                : [
                    ...this.value,
                    ...eventFiles
                ];

            this.changeFilesEmit(files);
        },
        onRemoveSelectedFileEvent(file) {
            const filesClone = [...cloneDeep(this.getFilesToUpload)];

            const tmpFiles = filesClone.filter((_, index) => index !== filesClone.indexOf(file));

            this.changeFilesEmit([
                ...tmpFiles,
                ...this.getUploadedFiles
            ]);
        },
        dragover(event) {
            event.preventDefault();

            // Add some visual fluff to show the user can drop its files
            if (!event.currentTarget.classList.contains('dragging')) {
                event.currentTarget.classList.add('dragging');
            }
        },
        dragleave(event) {
            // Clean up
            event.currentTarget.classList.remove('dragging');
        },
        drop(event) {
            event.preventDefault();

            // Trigger the onChange event manually
            this.onFileChangeEvent(event);

            event.currentTarget.classList.remove('dragging');
        },
        // emits
        onUploadedFileRemoveEvent(file) {
            this.$emit('remove-uploaded-file', file);
        },
        onUploadedFileDownloadEvent(file) {
            this.$emit('download-uploaded-file', file);
        },
        changeFilesEmit(files) {
            this.$emit('update', this.name, files);
        },
        onFilePreviewEvent(file) {
            this.$emit('preview-uploaded-file', file);
        },
    },
}
</script>